import React, { useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import * as Raven from 'raven-js';
import { useForm } from 'react-hook-form';

import {
  BackLink,
  Button,
  TextField,
  Typography,
} from '../../components/atoms';
import { Warning as WarningIcon } from '../../components/icons';
import { BottomBar, LoadingSpinner, Page } from '../../components/layouts';
import { AboutMyDataDialog } from '../../components/partials';
import { Track } from '../../weborder/analytics';
import {
  DispatchProps,
  StateProps,
} from '../../weborder/component/CustomLocation';
import * as Data from '../../weborder/Data';
import * as DeliveryLocations from '../../weborder/DeliveryLocations';
import { Utilities } from '../../weborder/Utilities';
import { domUtils } from '../../weborder/utils';

const useStyles = makeStyles((theme: Theme) => ({
  aboutMyData: {
    paddingRight: theme.spacing(2),
    textAlign: 'right',
    marginBottom: theme.spacing(9),
  },

  confirmButton: {
    display: 'none',
  },

  map: {
    boxSizing: 'content-box',
    height: '160px',
    marginBottom: theme.spacing(2),
    width: '100%',
  },

  row: {
    margin: theme.spacing(0, 2, 2.5),
  },

  [theme.breakpoints.up('sm')]: {
    aboutMyData: {
      paddingRight: theme.spacing(3),
    },

    map: {
      border: `1px solid ${theme.palette.grey[400]}`,
      height: '200px',
      marginBottom: theme.spacing(2.5),
    },

    row: {
      margin: theme.spacing(0, 3, 2.5),
    },
  },

  [theme.breakpoints.up('md')]: {
    aboutMyData: {
      paddingRight: 0,
      marginBottom: 0,
    },

    confirmButton: {
      display: 'block',
      marginBottom: theme.spacing(2.5),
    },

    row: {
      margin: theme.spacing(0, 0, 2.5),
      '&:last-of-type': {
        marginBottom: theme.spacing(5.5),
      },
    },
  },
}));

interface Route {
  params: {
    lat: string;
    lng: string;
  };
}

type Props = DispatchProps & StateProps & Route;

interface FormInputs {
  building: string;
  instructions: string;
  postcode: string;
  street: string;
  town: string;
}

export function DeliveryConfirmLocation(props: Props): JSX.Element {
  const classes = useStyles();

  const {
    addressSuggestion,
    AppConfig: appConfig,
    Config: config,
    LoadDeliveryLocations: loadDeliveryLocations,
    LocationGeo: locationGeo,
    params,
    translate,
  } = props;

  const center = locationGeo;

  const [showAboutMyData, setShowAboutMyData] = useState(false);

  const [defaultAddress, setDefaultAddress] = useState({
    building: '',
    instructions: '',
    postcode: '',
    street: '',
    town: '',
  });

  const { errors, handleSubmit, register, reset } = useForm<FormInputs>({
    defaultValues: defaultAddress,
  });

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    config.MapLoader.then(initMap);
  }, []);

  useEffect(() => {
    reset({
      building: defaultAddress?.building,
      postcode: defaultAddress?.postcode,
      street: defaultAddress?.street,
      town: defaultAddress?.town,
    });
  }, [defaultAddress]);

  const { enqueueSnackbar } = useSnackbar();

  const initMap = (tools: Data.GoogleTools) => {
    const query = {
      location: {
        lat: parseFloat(params.lat),
        lng: parseFloat(params.lng),
      },
    };
    tools.GeoCoder.geocode(query, handleGeocodeResponse);

    const options: google.maps.MapOptions = {
      center: Data.LatLng(locationGeo),
      clickableIcons: false,
      draggable: false,
      mapTypeControl: false, // No map type (terrain/satelite, etc)
      streetViewControl: false, // No street view ControlLabel
      zoom: 17,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
    };

    const map = new google.maps.Map(
      document.getElementById('mapRef') as Element,
      options,
    );

    new google.maps.Marker({
      position: Data.LatLng(locationGeo),
      map,
    });
  };

  const handleOnClickAboutMyData = () => {
    setShowAboutMyData(!showAboutMyData);
  };

  const handleGeocodeResponse = (
    results: google.maps.GeocoderResult[],
    status: google.maps.GeocoderStatus,
  ): JSX.Element | null => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Geocode results:', results);
    }

    if (appConfig.status !== 'success') {
      return null;
    }

    if (addressSuggestion.suggestion && !params.lat && !params.lng) {
      // https://trello.com/c/57UyvrHD/1029-new-delivery-location-round-2
      // We don't want to trust the results of the Google Places API call.
      // Sometimes the result of the places API call is not as descriptive as the
      // input.
      // Conor's Address ("Parkfield House, Dublin, Ireland"), for example, will
      // be returned as "Mount Merrion, Ireland".
      // Instead we take the string that the user click on, and split it on comma.
      // We don't just take the first 3 elements though - we combine all address
      // fields after the third into the third.

      const suggestion = addressSuggestion.suggestion;
      const [
        buildingValue,
        streetValue,
        ...remainingAddressDetails
      ] = suggestion.split(',').map((s) => s.trim());

      if (remainingAddressDetails.length > 1) {
        remainingAddressDetails.pop();
      }
      const townValue = remainingAddressDetails.join(', ');
      const postcode = appConfig.data.PostCodeInputLabel
        ? addressSuggestion.postalCode
        : '';

      // setGeocodeResults(results);
      setDefaultAddress({
        building: buildingValue,
        instructions: '',
        postcode: postcode,
        street: streetValue,
        town: townValue,
      });
    } else {
      if (status !== google.maps.GeocoderStatus.OK) {
        Raven.captureMessage('Error loading geocode response', {
          tags: { category: 'Third party library failure' },
          extra: {
            status: status,
            results: results,
          },
        });
        return;
      }

      if (results.length === 0) {
        return;
      }

      try {
        const addressComponents = results[0].address_components;

        let a = '';
        let roomNumber = '';
        let streetNumber = '';

        for (const addressComponent of addressComponents) {
          if (addressComponent.types[0] === 'room') {
            roomNumber = addressComponent.long_name;
            break;
          }
        }

        for (const addressComponent of addressComponents) {
          if (addressComponent.types[0] === 'street_number') {
            streetNumber = addressComponent.long_name;
            break;
          }
        }

        a = roomNumber;

        if (streetNumber) {
          if (appConfig.data.DeliveryAddressLayout === 2) {
            a = streetNumber;
          } else if (a !== '') {
            a = `${a}, ${streetNumber}`;
          } else {
            a = streetNumber;
          }
        }

        let b = '';
        let route = '';
        let locality = '';

        for (const addressComponent of addressComponents) {
          if (addressComponent.types[0] === 'route') {
            route = addressComponent.long_name;
            break;
          }
        }

        for (const addressComponent of addressComponents) {
          if (
            addressComponent.types[0] === 'neighborhood' ||
            addressComponent.types[0] === 'sublocality'
          ) {
            locality = addressComponent.long_name;
            break;
          }
        }

        b = route;

        if (locality) {
          if (b !== '') {
            b = `${b}, ${locality}`;
          } else {
            b = locality;
          }
        }

        let c = '';
        for (const addressComponent of addressComponents) {
          if (
            addressComponent.types[0] === 'locality' ||
            addressComponent.types[0] === 'postal_town'
          ) {
            c = addressComponent.long_name;
            break;
          }
        }

        let d = '';

        if (appConfig.data.PostCodeInputLabel) {
          for (const addressComponent of addressComponents) {
            if (addressComponent.types[0] === 'postal_code') {
              d = addressComponent.long_name;
            }
          }
        }

        let buildingValue =
          appConfig.data.DeliveryAddressLayout === 2
            ? ''
            : a || defaultAddress?.building;
        let streetValue =
          appConfig.data.DeliveryAddressLayout === 2 && a
            ? `${a} ${b}`
            : b || defaultAddress?.street;
        let townValue = c || defaultAddress?.town;
        const postcodeValue = d || defaultAddress?.postcode;

        if (buildingValue.startsWith('Unnamed ')) {
          buildingValue = '';
        }

        if (streetValue.startsWith('Unnamed ')) {
          streetValue = '';
        }

        if (townValue.startsWith('Unnamed ')) {
          townValue = '';
        }

        // setGeocodeResults(results);
        setDefaultAddress({
          building: buildingValue,
          instructions: '',
          postcode: postcodeValue,
          street: streetValue,
          town: townValue,
        });
      } catch (e) {
        Raven.captureMessage('Address splitting failed', {
          tags: { category: 'Frontend error' },
          extra: {
            exception: e,
            addressComponents: results[0].address_components,
          },
        });
      }
    }
  };

  if (appConfig.status !== 'success') {
    return <LoadingSpinner />;
  }

  const errorsLength = Object.keys(errors).length;
  if (errorsLength > 0) {
    enqueueSnackbar(translate('Please_fill_all_required_fields'));
  }

  const onSubmit = (data: FormInputs) =>
    DeliveryLocations.Create(config.DeviceId, {
      AutoSelectThisLocation: false,
      Building: data.building,
      Coordinates: center,
      DeliveryInstructions: data.instructions,
      DeliveryLocationId: -1,
      IsCustomAddress: true,
      IsDisplayed: true,
      PostCode: data.postcode,
      Street: data.street,
      Town: data.town,
    }).then((result) => {
      Track('Maps', 'Address Confirmed');

      loadDeliveryLocations();

      const id = result.data.Data;
      Utilities.navigateTo(`/delivery/${id}`);
    });

  const fieldText = {
    BuildingOrApartment: [
      translate('Building_or_apartment_number'),
      translate('Line_1'),
    ],
    streetName: [translate('Street_name'), translate('Street')],
    cityOrTown: ['City', 'Town'],
    postCode: [translate('Post_code'), appConfig.data.PostCodeInputLabel],
  };

  const addressLayoutKey = appConfig.data.DeliveryAddressLayout === 2 ? 0 : 1;

  const getWarningIcon = (fieldError) => (fieldError ? <WarningIcon /> : null);

  const buildingField = (
    <TextField
      aria-invalid={!!errors.building}
      aria-required="true"
      data-fd="custom-building"
      endIcon={getWarningIcon(errors.building)}
      error={!!errors.building}
      helperText={
        errors.building
          ? translate('ConfirmAddress_inputField_requiredWarning')
          : ''
      }
      id="BuildingOrApartment"
      inputRef={register({
        required: true,
      })}
      key="building"
      label={fieldText['BuildingOrApartment'][addressLayoutKey]}
      name="building"
      placeholder={translate('Building_or_apartment_number')}
      status={errors.building ? 'error' : 'default'}
    />
  );

  const streetField = (
    <TextField
      aria-invalid={!!errors.street}
      aria-required="true"
      data-fd="custom-street"
      endIcon={getWarningIcon(errors.street)}
      error={!!errors.street}
      helperText={
        errors.street
          ? translate('ConfirmAddress_inputField_requiredWarning')
          : ''
      }
      id="streetName"
      inputRef={register({
        required: true,
      })}
      key="street"
      label={fieldText['streetName'][addressLayoutKey]}
      name="street"
      placeholder={translate('Street_name')}
      status={errors.street ? 'error' : 'default'}
    />
  );

  const addressFields = [0, 7].includes(appConfig.data.DeliveryAddressLayout)
    ? [buildingField, streetField]
    : [streetField, buildingField];

  return (
    <>
      <Page pageName="delivery-confirm-location">
        <header>
          <BackLink
            label={translate('Back')}
            linkPath="/delivery"
            testId="custom-back"
          />

          <Typography id="skip-to-content" tabIndex={0} variant="h1">
            {translate('Confirm_address')}
          </Typography>
        </header>

        <article>
          <div aria-hidden="true" id="mapRef" className={classes.map} />

          <form onSubmit={handleSubmit(onSubmit)}>
            {addressFields.map((field, index) => (
              <div key={index} className={classes.row}>
                {field}
              </div>
            ))}

            <div className={classes.row}>
              <TextField
                aria-invalid={!!errors.town}
                aria-required="true"
                data-fd="custom-town"
                endIcon={getWarningIcon(errors.town)}
                error={!!errors.town}
                helperText={
                  errors.town
                    ? translate('ConfirmAddress_inputField_requiredWarning')
                    : ''
                }
                id="cityOrTown"
                inputRef={register({
                  required: true,
                })}
                key="town"
                label={fieldText['cityOrTown'][addressLayoutKey]}
                name="town"
                placeholder={fieldText['cityOrTown'][addressLayoutKey]}
                status={errors.town ? 'error' : 'default'}
              />
            </div>

            {appConfig.data.PostCodeInputLabel && (
              <div className={classes.row}>
                <TextField
                  aria-invalid={!!errors.postcode}
                  aria-required="true"
                  data-fd="custom-postcode"
                  endIcon={getWarningIcon(errors.postcode)}
                  error={!!errors.postcode}
                  helperText={
                    errors.postcode
                      ? translate('ConfirmAddress_inputField_requiredWarning')
                      : ''
                  }
                  id="postCode"
                  inputRef={register({
                    required: true,
                  })}
                  key="postcode"
                  label={fieldText['postCode'][addressLayoutKey]}
                  name="postcode"
                  placeholder={fieldText['postCode'][addressLayoutKey]}
                  status={errors.postcode ? 'error' : 'default'}
                />
              </div>
            )}

            <div className={classes.row}>
              <TextField
                data-fd="custom-instructions"
                id="instructions"
                key="instructions"
                label={translate('Delivery_instructions_optional')}
                multiline
                placeholder={translate(
                  'Any_special_instructions_for_our_delivery_driver',
                )}
                rows={3}
              />
            </div>

            <Button
              className={classes.confirmButton}
              data-fd="address-confirm"
              fullWidth
              key="confirm-button"
              type="submit"
            >
              {translate('Confirm_address')}
            </Button>
          </form>

          <div className={classes.aboutMyData}>
            <Button
              className="noUnderline"
              onClick={handleOnClickAboutMyData}
              variant="text"
            >
              {translate('About_my_data')}
            </Button>
          </div>
        </article>
      </Page>

      {showAboutMyData && (
        <AboutMyDataDialog
          handleOnClick={handleOnClickAboutMyData}
          showDialog={showAboutMyData}
          translate={translate}
        />
      )}

      <BottomBar fixedOnly={['xs', 'sm']}>
        <Button
          data-fd="address-confirm-bottom"
          fullWidth
          key="confirm-button-bottom"
          onClick={handleSubmit(onSubmit)}
          type="submit"
        >
          {translate('Confirm_address')}
        </Button>
      </BottomBar>
    </>
  );
}
